import React, { useEffect, useMemo, useRef, useState } from "react";
import Input from "theme/Input";
import Text from "theme/Text";
import _ from "lodash";
import i18n from "utils/i18n";
import { StyleSheet, View } from "react-native";
import FormRow from "components/organisms/form/FormRow";
import ThemedSelect from "components/atoms/ThemedSelect";
import ThemedTimePicker from "components/atoms/ThemedTimePicker";
import SelectLunch from "components/molecules/SelectLunch";
import validateFormValues from 'utils/form-validation';
import { constantsActive } from "utils/constants/constants";
import ThemedDivider from "components/atoms/ThemedDivider";
import TimeslotCreate from 'components/management/molecules/TimeslotCreate'
import HelperPlatform from "utils/helper-platform";
import HelperAvailability from "utils/helper/HelperAvailability";
import HelperString from 'utils/helper-string'
import FormSubmitButton from 'components/atoms/FormSubmitButton'
import { useIsSmallDevice } from 'utils/helper-device'
import ImagePicker from 'components/management/molecules/ThemedImagePicker'
import ThemedPressable from 'components/atoms/ThemedPressable'
import { FormStyles } from "styles/components/molecules/Form";
import { FormFieldsConstants, FormStatus } from 'utils/constants/form-constants';
import { colorList } from 'utils/colors'
import { ThemedShared } from 'styles/themed'
import ThemedTimeslotTimeSelect from 'components/atoms/ThemedTimeslotTimeSelect'
import ThemedRangeDatepicker from 'components/atoms/ThemedRangeDatepicker'
import ThemedDatepicker from 'components/atoms/ThemedDatepicker'
import { Column, Grid, Row } from 'components/atoms/BootstrapGrid'
import VStack from 'components/atoms/stack/VStack'
import FormAddress from 'components/organisms/form/Address'
import ThemedSelectMulti from 'components/atoms/ThemedSelectMulti'
import Icon from "components/atoms/Icon";
import { IconSystemConstants } from "utils/constants/IconNameConstants";
import HStack from "components/atoms/stack/HStack";

export { createTranslationField, retrieveTranslationFields, createTranslationMutationParameters } from 'utils/form-translations';

export const createTimeOptions = ( option, { translation } = {} ) => {
	const optionSplit = option.split(':');
	
	const optionKey = optionSplit[ 0 ] ?? '';
	const optionValue = parseInt( optionSplit[ 1 ] ?? 0 );
	
	let value = i18n.t(translation ? translation : `management:form.time_select.none` );
	if( !translation && optionValue !== 0 ) {
		value = i18n.t(`management:form.time_select.${optionKey}`, {
			number: optionValue,
			count: optionValue
		} );
	}
	
	return {
		value: option,
		label: value
	}
}

export const FormLayout = {
	DEFAULT: 'default',
	VERTICAL: 'vertical',
}

const Form = ({ fields = {}, layout = FormLayout.DEFAULT, values = null, onChange, onSubmit = null, isSubmitting = false, isSubmitted = false, onlyFields = false, forceSaveButton = false, disabled = false, hideSubmit = false, disabledSubmit = false, children }) => {
	
	const isSmallDevice = useIsSmallDevice();
	
	const constraints = useRef( {} )
	const formValues = useRef( _.cloneDeep( values ) )
	
	const [ originalFormValues, setOriginalFormValues ] = useState( _.cloneDeep( values ) )
	
	//const [ formValues, setFormValues ] = useState( _.cloneDeep( values ) )
	const [ formWarnings, setFormWarnings ] = useState( {} )
	const [ formStatus, setFormStatus ] = useState( FormStatus.NONE )

	const addConstraint = ( name, label, options ) => {
		_.forEach( options, ( o, k ) => {
			
			let transKey = 'form.warnings.invalid';
			if( k === 'presence' ) {
				transKey = 'form.warnings.empty';
			}
			
			o.message = '^'+ i18n.t( transKey, {
				field: label
			} )
		});
		
		constraints.current[ name ] = options;
	}
	
	const setFormValues = values => {
		formValues.current = values;
	}
	
	const getFormValue = (name, defaultValue) => {
		let value = _.get( formValues.current, name, defaultValue );
		
		if( value === null ) {
			value = defaultValue;
		}
		
		return value
	}
	
	const createField = ( field, force_disabled = false ) => {
		const label = _.get( field, 'label', '' );
		let hide_label = _.get( field, 'hide_label', false );
		
		let content = null;
		
		const fieldProps = {
			key: HelperString.hash(`form-${field.type}`, _.get( field, 'name', HelperString.random() ) ),
			disabled: !force_disabled ? _.get( field, 'disabled', false ) : true,
			style: [
				{ width: '100%' },
				_.get( field, 'style', {} )
			],
			..._.get( field, 'props', {} )
		}
		
		let constraintOptions = {};
		if( _.get( field, 'required', false ) === true ) {
			constraintOptions = {
				presence: {
					allowEmpty: false
				},
			}
		}
		
		switch( field.type ) {
			case FormFieldsConstants.TITLE:
				hide_label = true;
				content = (
					<Text
						key={ HelperString.hash('form-title', label ) }
						{ ...fieldProps }
						category={"h6"}>

						{ label }
					</Text>
				);
				break;
			
			case FormFieldsConstants.DIVIDER:
				hide_label = true;
				content = (
					<ThemedDivider
						// { ...fieldProps }
					/>
				);
				break;
			
			case FormFieldsConstants.SELECT:
				content = (
					<ThemedSelect
						{ ...fieldProps }
						placeholder={ _.get( field, 'placeholder', null ) }
						options={ field.options }
						select={ ( selected ) => {
							return setFormValue(field.name, selected);
						} }
						selected={ getFormValue( field.name, '' ) }
					/>
				)
				break;
			
			case FormFieldsConstants.TIMESLOT_TIME_SELECT:
				content = (
					<ThemedTimeslotTimeSelect
						{ ...fieldProps }
						placeholder={ _.get( field, 'placeholder', null ) }
						options={ field.options }
						select={ ( selected ) => {
							return setFormValue(field.name, selected);
						} }
						selected={ getFormValue( field.name, '' ) }
					/>
				)
				break;
			
			case FormFieldsConstants.SELECT_MULTI:
				content = (
					<ThemedSelectMulti
						{ ...fieldProps }
						options={ field.options }
						placeholder={ null }
						select={ ( selected ) => {
							return setFormValue(field.name, selected);
						} }
						selected={ getFormValue( field.name, [] ) }
					/>
				)
				break;
			
			case FormFieldsConstants.SELECT_LUNCH:
				content = (
					<SelectLunch
						{ ...fieldProps }
						placeholder={ null }
						options={ field.options }
						select={ ( {selected} ) => {
							return setFormValue(field.name, selected);
						} }
						selected={ getFormValue( field.name, '' ) }
					/>
				)
				break;
			
			case FormFieldsConstants.SELECT_ACTIVE:
				content = (
					<ThemedSelect
						{ ...fieldProps }
						placeholder={ null }
						options={[
							{
								value: constantsActive.YES,
								label: i18n.t('basic.yes')
							},
							{
								value: constantsActive.NO,
								label: i18n.t('basic.no')
							}
						]}
						select={ ( selected ) => {
							return setFormValue(field.name, selected);
						} }
						selected={ getFormValue( field.name, constantsActive.NO ) }
					/>
				)
				break;
			
			case FormFieldsConstants.SELECT_AVAILABLE_FOR:
				content = (
					<ThemedSelect
						{ ...fieldProps }
						placeholder={ null }
						options={[
							{
								value: HelperAvailability.AVAILABLE_FOR_EVERYONE,
								label: i18n.t('basic.available_for.everyone')
							},
							{
								value: HelperAvailability.AVAILABLE_FOR_RESTRICTED,
								label: i18n.t('basic.available_for.restricted')
							},
							{
								value: HelperAvailability.AVAILABLE_FOR_RESTRICTED_EMPLOYEES,
								label: i18n.t('basic.available_for.restricted_employees')
							}
						]}
						select={ ( selected ) => {
							return setFormValue(field.name, selected);
						} }
						selected={ getFormValue( field.name, '' ) }
					/>
				)
				break;
			
			case FormFieldsConstants.TIME:
				content = (
					<ThemedTimePicker
						{ ...fieldProps }
						time={ getFormValue( field.name, '00:00:00' ) }
						onSelect={ ( time ) => {
							return setFormValue(field.name, time);
						} }
					/>
				)
				break;
			
			case FormFieldsConstants.NUMBER:
				content = (
					<Input
						{ ...fieldProps }
						keyboardType={ HelperPlatform.isIOS() ? "phone-pad" : "number-pad" }
						defaultValue={ String( getFormValue( field.name, '' ) ) }
						blurOnSubmit={ false }
						onChangeText={ ( value ) => {
							value = value.replace( /[^0-9,\.\-]/g, '' );

							// Replace , for .
							value = value.replace( /,/g, '.' );

							// Remove every . except the last
							value = value.replace( /\.(?=.*\.)/g, '' );

							setFormValue( field.name, Number( value ) )
						} }
					/>
				);
				break;
			
			case FormFieldsConstants.ADDRESS:
				content = (
					<FormAddress
						{ ...fieldProps }
						field={ field }
						getFormValue={ getFormValue }
						onChange={ ({ value, isValid }) => {
							setFormValue( field.name, isValid ? value : 'error' )
						} }
					/>
				);
				break;
			
			case FormFieldsConstants.TEXT_EMAIL:
				constraintOptions = {
					presence: {
						allowEmpty: false
					},
					email: {}
				};
				
				content = (
					<Input
						{ ...fieldProps }
						autoCorrect={ false }
						keyboardType={'email-address'}
						defaultValue={ String( getFormValue( field.name, '' ) ) }
						blurOnSubmit={ false }
						onChangeText={ ( value ) => {
							return setFormValue(field.name, value?.toLowerCase() ?? '');
						} }
					/>
				);
				break;
			
			case FormFieldsConstants.AUTOCOMPLETE:
				const options = _.get( field, 'options', {} );
				
				content = (
					<View>
						<Input
							{ ...fieldProps }
							defaultValue={ String( getFormValue( field.name, '' ) ) }
							blurOnSubmit={ false }
							autoCorrect={ false }
							onChangeText={ _.get( field, 'onChangeText', null ) }
						/>
						
						{/*	 results? */}
						{ !_.isEmpty( options ) && (
							<View
								style={staticStyles.viewBackgroundColorPrimaryBorderWidth1}>
								
								{ _.map( options, ( item, guid ) => {
									
									const onSelect = _.get( field, 'onSelect', () => {
									} )
									
									return (
										<ThemedPressable
											onPress={ onSelect.bind( this, guid ) }>
											
											<View
												style={staticStyles.viewFlexDirectionRowAlignItemsCenter}>
												
												<HStack 
													flex={1} 
													justifyContent={"space-between"}>

													<Text>{ item.title }</Text>

													{ item.isChecked === true && (
														<View style={staticStyles.viewFlex1}>
															<Icon 
																icon={ IconSystemConstants.CHECKBOX }
																checked={ true }
																color={ colorList.ORANGE.primary }
																style={staticStyles.iconAlignSelfFlexEnd}
															/>
														</View>
													)}
												</HStack>
											</View>
										</ThemedPressable>
									)}
								)}
							</View>
						) }
					</View>
				);
				break;
			
			case FormFieldsConstants.TIMESLOT:
				content = (
					<TimeslotCreate
						FormComponent={ Form }
						{ ...fieldProps }
						name={ field.name }
						value={ getFormValue( field.name, {} ) }
						onChange={ ( data ) => {
							return setFormValue(field.name, data);
						} }
					/>
				);
				break;
			
			case FormFieldsConstants.TEXT_AREA:
				content = (
					<Input
						{ ...fieldProps }
						multiline={ true }
						numberOfLines={ fieldProps.numberOfLines ?? 4 }
						autoCorrect={ false }
						defaultValue={ String( getFormValue( field.name, '' ) ) }
						blurOnSubmit={ false }
						onChangeText={ ( value ) => {
							return setFormValue(field.name, value);
						} }
					/>
				);
				break;
			
			case FormFieldsConstants.EXPLANATION:
				hide_label = true;
				content = (
					<Text
						style={ staticStyles.textMarginLeft15FontSize10 }>
						
						{ field.explanation }
					</Text>
				)
				break;
			
			case FormFieldsConstants.IMAGE_PICKER:
				content = (
					<ImagePicker
					
					/>
				)
				break;
			
			case FormFieldsConstants.ITEM_PICKER:
				const selected = getFormValue( field.name, '' );
				const items = _.map( field.options, ( option ) => {
					const item = field.renderItem( option, selected === option.key, fieldProps.disabled );
					
					return (
						<ThemedPressable
							onPress={ () => {
								return fieldProps.disabled ? {} : setFormValue(field.name, option.value);
							}}>
							{ item }
						</ThemedPressable>
					);
				});
				
				content = (
					<View style={ FormStyles.itemPickerContainer }>
						{ items }
					</View>
				);
				break;
				
			case FormFieldsConstants.DATE_PICKER:
				content = (
					<View>
						<ThemedDatepicker
							onSelect={ ( range ) => {
								setFormValue( field.name, range )
							}}
						/>
					</View>
				)
				
				break;
				
			case FormFieldsConstants.DATE_RANGED_PICKER:
				content = (
					<View>
						<ThemedRangeDatepicker
							range={ field.range }
							onSelect={ ( range ) => {
								setFormValue( field.name, range )
								
								if( field.onSelect ) {
									field.onSelect( range )
								}
							}}
						/>
					</View>
				)
				
				break;
			
			case FormFieldsConstants.TEXT:
			default:
				content = (
					<Input
						{ ...fieldProps }
						defaultValue={ String( getFormValue( field.name, '' ) ) }
						blurOnSubmit={ false }
						autoCorrect={ false }
						onChangeText={ value => {
							return setFormValue(field.name, value);
						} }
					/>
				);
		}
		
		// Add constraints
		if( !_.isEmpty( constraintOptions ) ) {
			addConstraint( field.name, label, constraintOptions );
		}
		
		return (
			<FormRow
				key={ HelperString.hash( 'formrow', fieldProps.key ) }
				title={ !hide_label ? label : '' }
				hint={ _.get( field, 'hint', null ) }
				warning={ _.get( formWarnings, field.name, null ) }>
				
				{ content }
			</FormRow>
		)
	}
	
	const setFormValue = ( key, value ) => {
		// Get the current values
		let newFormValues = _.cloneDeep( formValues.current )
		
		// Add the new value to the cloned values
		newFormValues[ key ] = value;
		
		// Validate
		const validationResult = validateFormValues( newFormValues, constraints.current );
		
		setFormValues( newFormValues )
		setFormWarnings( validationResult )
		setFormStatus( FormStatus.NONE );
		
		if( onChange ) {
			onChange( newFormValues, validationResult );
		}
	};
	
	const onPressSubmit = () => {
		if( disabled || disabledSubmit ) {
			return;
		}

		const validationResult = validateFormValues( formValues.current ?? {}, constraints.current );
		setFormWarnings( validationResult );
		if( validationResult ) {
			return;
		}

		if( _.isFunction( onSubmit ) ) {
			onSubmit( formValues.current, {
				callback: onSubmitCallback.bind( this )
			} );
		}
	}
	
	const onSubmitCallback = ( status ) => {
		setFormStatus( status )
	}
	
	useEffect( () => {
		setOriginalFormValues( _.cloneDeep( values ) )
	}, [ values ] )
	
	const hasFormWarnings = _.size( formWarnings ) > 0;
	const isChanged = !_.isEqual( originalFormValues, formValues.current );
	
	const fieldsElement = useMemo( () => {
		return (
			<VStack>
				{ _.map( fields, (field, key) => {
					return createField( field, disabled )
				} ) }

				{ children }
			</VStack>
		);
	}, [ fields, children, formWarnings, disabled ] )
	
	// When only fields, return now
	if( onlyFields ) {
		return fieldsElement;
	}
	
	// Create the submit button
	let submitButton = null;
	
	if( hideSubmit === false && ( !disabled || forceSaveButton ) ) {
		submitButton = (
			<FormSubmitButton
				formStatus={ formStatus }
				onPress={ onPressSubmit }
				formStatusCallback={ setFormStatus }
				disabled={ disabledSubmit || ( formStatus !== FormStatus.SUCCESS && (( !isChanged || hasFormWarnings ) && !forceSaveButton )) }
			/>
		)
	}
	
	if( layout === FormLayout.DEFAULT && HelperPlatform.isWeb() && !isSmallDevice ) {
		return (
			<Grid>
				<Row>
					<Column
						style={ FormStyles.fieldsWeb }>
						
						{ fieldsElement }
					</Column>
					
					<Column
						style={ FormStyles.submit }>
						
						{ submitButton }
					</Column>
				</Row>
			</Grid>
		)
	}
	
	return (
		<View>
			{ fieldsElement }
			
			<View
				style={ layout === FormLayout.DEFAULT ? FormStyles.submit : FormStyles.submitSmall }>
				
				{ submitButton }
			</View>
		</View>
	)
}

const staticStyles = StyleSheet.create({
	viewBackgroundColorPrimaryBorderWidth1: {
		backgroundColor: colorList.WHITE.primary,
		borderWidth: 1,
		borderColor: colorList.GREY.light,
		borderRadius: ThemedShared.borderRadius,
	},
	viewFlexDirectionRowAlignItemsCenter: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 12,
		paddingHorizontal: 8,
	},
	viewFlex1: {
		flex: 1,
	},
	iconAlignSelfFlexEnd: {
		alignSelf: 'flex-end',
	},
	textMarginLeft15FontSize10: {
		marginLeft: 15,
		fontSize: 10,
	},
});

export default Form
